// Quản lý công việc: giao việc, theo dõi, báo hoàn thành và duyệt kết quả
import React, { useMemo, useState } from 'react';
import { Button, Empty, Input, Segmented, Select, Table, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useSearchParams } from 'react-router-dom';
import { CheckSquare, ClipboardCheck, Eye, Inbox, LayoutGrid, ListTodo, Plus, Repeat, Send, UserX } from 'lucide-react';
import Layout from '../../Layout';
import { DEPARTMENTS, ME_ID, PROJECTS, memberName, projectOf, type TaskStatus, type WorkTask } from './data';
import { ACTION_LABEL, allowedActions, isClosed, isOverdue, type Action } from './flow';
import useWorkStore from './store';
import TaskDetail from './TaskDetail';
import TaskForm, { memberOptions } from './TaskForm';
import { GanttView } from './component/GanttView';
import { Avatar, CheckCount, DueText, PriorityTag, RepeatTag, StatusTag } from './ui';

type ViewKey = 'mine' | 'assigned' | 'approve' | 'follow' | 'unassigned' | 'repeat' | 'all';
type StatusFilter = 'open' | 'overdue' | 'review' | 'done' | 'all';

const VIEWS: { key: ViewKey; label: string; icon: React.ReactNode; hint: string; test: (t: WorkTask) => boolean }[] = [
  { key: 'mine', label: 'Việc của tôi', icon: <Inbox size={16} />, hint: 'Việc bạn là người làm', test: t => t.assigneeId === ME_ID },
  { key: 'assigned', label: 'Tôi giao', icon: <Send size={16} />, hint: 'Việc bạn đã giao cho người khác', test: t => t.creatorId === ME_ID },
  { key: 'approve', label: 'Cần tôi duyệt', icon: <ClipboardCheck size={16} />, hint: 'Người làm đã báo xong, chờ bạn duyệt', test: t => allowedActions(t, ME_ID).includes('approve') },
  { key: 'follow', label: 'Tôi theo dõi', icon: <Eye size={16} />, hint: 'Việc bạn được thêm vào theo dõi', test: t => t.followerIds.includes(ME_ID) },
  { key: 'unassigned', label: 'Chưa giao', icon: <UserX size={16} />, hint: 'Việc đang mở chưa có người làm', test: t => !t.assigneeId && !isClosed(t) },
  { key: 'repeat', label: 'Việc định kỳ', icon: <Repeat size={16} />, hint: 'Quy trình lặp lại (khử khuẩn, kiểm tra thuốc, bảo trì máy…) — đóng lần này hệ thống tự tạo lần sau', test: t => !!t.repeat },
  { key: 'all', label: 'Tất cả công việc', icon: <ListTodo size={16} />, hint: 'Toàn bộ công việc bạn được xem', test: () => true },
];

const STATUS_FILTERS: { value: StatusFilter; label: string; test: (t: WorkTask) => boolean }[] = [
  { value: 'open', label: 'Đang mở', test: t => !isClosed(t) },
  { value: 'overdue', label: 'Quá hạn', test: t => isOverdue(t) },
  { value: 'review', label: 'Chờ duyệt', test: t => t.status === 'review' },
  { value: 'done', label: 'Đã đóng', test: t => isClosed(t) },
  { value: 'all', label: 'Tất cả', test: () => true },
];

// Kéo thẻ giữa các cột = thao tác tương ứng trong luồng
const BOARD: { status: TaskStatus; label: string; color: string }[] = [
  { status: 'todo', label: 'Chưa bắt đầu', color: '#94a3b8' },
  { status: 'doing', label: 'Đang làm', color: '#3b82f6' },
  { status: 'review', label: 'Chờ duyệt', color: '#a855f7' },
  { status: 'done', label: 'Hoàn thành', color: '#22c55e' },
];

const dropAction = (from: TaskStatus, to: TaskStatus): Action | null => {
  if (from === 'todo' && to === 'doing') return 'start';
  if ((from === 'todo' || from === 'doing') && (to === 'review' || to === 'done')) return 'submit';
  if (from === 'review' && to === 'done') return 'approve';
  if (from === 'review' && to === 'doing') return 'return';
  return null;
};

const sortTasks = (a: WorkTask, b: WorkTask) =>
  Number(isClosed(a)) - Number(isClosed(b)) || Number(isOverdue(b)) - Number(isOverdue(a)) || (a.dueAt ?? '9').localeCompare(b.dueAt ?? '9');

function Board({ tasks, onOpen }: { tasks: WorkTask[]; onOpen: (id: string) => void }) {
  const act = useWorkStore(s => s.act);
  const [over, setOver] = useState<TaskStatus | null>(null);
  const drop = (id: string, to: TaskStatus) => {
    setOver(null);
    const t = tasks.find(x => x.id === id);
    if (!t || t.status === to) return;
    const a = dropAction(t.status, to);
    if (!a) { message.warning('Không chuyển trực tiếp được giữa hai cột này'); return; }
    if (!allowedActions(t, ME_ID).includes(a)) { message.warning(`Bạn không thể "${ACTION_LABEL[a]}" việc này`); return; }
    if (a === 'return') { message.info('Trả lại cần ghi lý do — nhập trong chi tiết công việc'); onOpen(t.id); return; }
    // Báo xong có thể cần tích checklist / kèm ảnh — mở chi tiết để làm tiếp
    if (a === 'submit' && (t.needProof || t.checklist?.some(c => !c.done))) { message.info('Hoàn tất checklist / ảnh kết quả trong chi tiết công việc'); onOpen(t.id); return; }
    const err = act(t.id, a, { by: ME_ID });
    if (err) message.warning(err);
    else message.success(a === 'submit' && t.needReview ? 'Đã báo hoàn thành — chờ người giao duyệt' : ACTION_LABEL[a]);
  };
  return (
    <div className="grid min-w-[900px] grid-cols-4 gap-3">
      {BOARD.map(col => {
        const list = tasks.filter(t => t.status === col.status);
        return (
          <div key={col.status} onDragOver={e => { e.preventDefault(); setOver(col.status); }} onDragLeave={() => setOver(null)}
            onDrop={e => drop(e.dataTransfer.getData('taskId'), col.status)}
            className={`flex max-h-[calc(100vh-260px)] flex-col rounded-xl border bg-slate-50 dark:bg-gray-900 ${over === col.status ? 'border-[#3e91ab]' : 'border-slate-200 dark:border-gray-700'}`}>
            <div className="flex items-center gap-2 px-3 py-2.5 text-sm font-semibold">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: col.color }} />{col.label}<span className="font-normal text-slate-500">{list.length}</span>
            </div>
            <div className="flex-1 space-y-2 overflow-y-auto px-2 pb-2">
              {list.slice(0, 60).map(t => (
                <div key={t.id} draggable onDragStart={e => e.dataTransfer.setData('taskId', t.id)} onClick={() => onOpen(t.id)}
                  className={`cursor-pointer rounded-lg border bg-white p-2.5 text-sm shadow-sm hover:border-[#3e91ab] dark:bg-gray-800 ${isOverdue(t) ? 'border-red-200' : 'border-slate-200 dark:border-gray-700'}`}>
                  <div className="mb-1 flex items-center justify-between gap-2 text-[11px] text-slate-400"><span className="font-mono">{t.code}</span><PriorityTag t={t} /></div>
                  <div className="font-medium text-slate-800 dark:text-gray-100">{t.name}</div>
                  <div className="truncate text-xs text-slate-500">{projectOf(t.projectId)?.name}</div>
                  <div className="mt-2 flex items-center justify-between gap-2 text-xs"><span className="flex items-center gap-2"><Avatar id={t.assigneeId} size={22} /><CheckCount t={t} /></span><DueText t={t} /></div>
                </div>
              ))}
              {list.length > 60 && <div className="py-1 text-center text-xs text-slate-400">+{list.length - 60} việc — dùng bộ lọc để thu hẹp</div>}
              {list.length === 0 && <div className="py-6 text-center text-xs text-slate-400">Kéo thẻ vào đây</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function TaskManager() {
  const tasks = useWorkStore(s => s.tasks);
  const act = useWorkStore(s => s.act);
  const [params, setParams] = useSearchParams();
  // view lấy thẳng từ URL để link từ menu / dashboard (?view=repeat) luôn đúng màn
  const view: ViewKey = VIEWS.some(v => v.key === params.get('view')) ? (params.get('view') as ViewKey) : 'mine';
  const setView = (v: ViewKey) => setParam('view', v);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('open');
  const [layout, setLayout] = useState<'list' | 'board' | 'gantt'>('list');
  const [q, setQ] = useState('');
  const [assignee, setAssignee] = useState<string | undefined>();
  const [creating, setCreating] = useState(false);
  const projectId = params.get('project') ?? undefined;
  const taskId = params.get('task');

  const setParam = (key: string, value?: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: key !== 'task' });
  };
  const openTask = (id: string) => setParam('task', id);

  const viewDef = VIEWS.find(v => v.key === view) ?? VIEWS[0];
  const scoped = useMemo(() => tasks.filter(t => (!projectId || t.projectId === projectId)), [tasks, projectId]);
  const counts = useMemo(() => Object.fromEntries(VIEWS.map(v => [v.key, scoped.filter(t => v.test(t) && (v.key === 'approve' || !isClosed(t))).length])), [scoped]);
  const projectCounts = useMemo(() => {
    const m = new Map<string, number>();
    tasks.forEach(t => { if (!isClosed(t)) m.set(t.projectId, (m.get(t.projectId) ?? 0) + 1); });
    return m;
  }, [tasks]);

  const base = scoped.filter(t => viewDef.test(t) && (!assignee || t.assigneeId === assignee) && (!q || `${t.code} ${t.name}`.toLowerCase().includes(q.toLowerCase())));
  const statusCount = (s: StatusFilter) => base.filter(STATUS_FILTERS.find(x => x.value === s)!.test).length;
  const shown = (layout === 'board' ? base.filter(t => t.status !== 'failed' && t.status !== 'cancelled') : base.filter(STATUS_FILTERS.find(x => x.value === statusFilter)!.test)).sort(sortTasks);

  const columns: ColumnsType<WorkTask> = [
    {
      key: 'name', title: 'Công việc', render: (_, t) => {
        const subs = tasks.filter(s => s.parentId === t.id);
        return (
          <span className="block min-w-[260px]">
            <span className={`font-medium ${t.status === 'cancelled' ? 'text-slate-400 line-through' : ''}`}>{t.name}</span>
            <span className="ml-2 inline-flex gap-1"><PriorityTag t={t} /><RepeatTag t={t} /></span>
            <div className="flex flex-wrap items-center gap-x-1 text-xs text-slate-500">
              <span className="font-mono">{t.code}</span> · {projectOf(t.projectId)?.name}
              {t.checklist?.length ? <> · <CheckCount t={t} /></> : null}
              {t.parentId && ' · việc con'}
              {subs.length > 0 && ` · ${subs.filter(isClosed).length}/${subs.length} việc con xong`}
            </div>
          </span>
        );
      },
    },
    { key: 'assignee', title: 'Người làm', width: 200, render: (_, t) => <Avatar id={t.assigneeId} withName /> },
    { key: 'creator', title: 'Người giao', width: 190, responsive: ['xl'], render: (_, t) => <span className="text-slate-600">{memberName(t.creatorId)}</span> },
    { key: 'due', title: 'Hạn chót', width: 120, render: (_, t) => <DueText t={t} /> },
    { key: 'status', title: 'Trạng thái', width: 130, render: (_, t) => <StatusTag t={t} /> },
    {
      key: 'next', title: '', width: 130, align: 'right', render: (_, t) => {
        const a = allowedActions(t, ME_ID).find(x => x === 'start' || x === 'submit' || x === 'approve');
        if (!a) return null;
        return (
          <Button size="small" type={a === 'approve' ? 'primary' : 'default'} onClick={e => {
            e.stopPropagation();
            if (a === 'start') { act(t.id, 'start', { by: ME_ID }); message.success(`${t.code}: Đang làm`); } else openTask(t.id);
          }}>{a === 'start' ? 'Bắt đầu' : a === 'submit' ? 'Báo xong' : 'Duyệt'}</Button>
        );
      },
    },
  ];

  const project = projectOf(projectId);

  return (
    <Layout>
      <div className="flex gap-4">
        <aside className="hidden w-60 shrink-0 lg:block">
          <div className="sticky top-0 space-y-4">
            <Button type="primary" block icon={<Plus size={16} />} onClick={() => setCreating(true)}>Tạo công việc</Button>
            <nav className="rounded-xl border border-slate-200 bg-white p-1.5 dark:border-gray-700 dark:bg-gray-800">
              {VIEWS.map(v => (
                <button key={v.key} onClick={() => setView(v.key)}
                  className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm ${view === v.key ? 'bg-[#e8f4f7] font-semibold text-[#2c6b7f]' : 'text-slate-600 hover:bg-slate-50 dark:text-gray-300 dark:hover:bg-gray-700'}`}>
                  {v.icon}<span className="flex-1">{v.label}</span>
                  {counts[v.key] > 0 && <span className={`rounded-full px-1.5 text-xs ${v.key === 'approve' ? 'bg-purple-100 text-purple-700' : 'text-slate-400'}`}>{counts[v.key]}</span>}
                </button>
              ))}
            </nav>
            <div className="rounded-xl border border-slate-200 bg-white p-1.5 dark:border-gray-700 dark:bg-gray-800">
              <div className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Dự án & phòng ban</div>
              <button onClick={() => setParam('project', null)} className={`flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left text-sm ${!projectId ? 'bg-slate-100 font-semibold dark:bg-gray-700' : 'hover:bg-slate-50 dark:hover:bg-gray-700'}`}>
                <LayoutGrid size={14} /> Tất cả
              </button>
              {DEPARTMENTS.map(d => (
                <div key={d.id}>
                  {PROJECTS.filter(p => p.departmentId === d.id).map(p => (
                    <button key={p.id} onClick={() => setParam('project', p.id)} title={p.name}
                      className={`flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left text-sm ${projectId === p.id ? 'bg-slate-100 font-semibold dark:bg-gray-700' : 'text-slate-600 hover:bg-slate-50 dark:text-gray-300 dark:hover:bg-gray-700'}`}>
                      <span className={`h-2 w-2 shrink-0 rounded-sm ${p.type === 'team' ? 'bg-slate-400' : 'bg-[#3e91ab]'}`} />
                      <span className="flex-1 truncate">{p.name}</span>
                      <span className="text-xs text-slate-400">{projectCounts.get(p.id) ?? ''}</span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </aside>

        <main className="min-w-0 flex-1 space-y-3">
          <div className="flex flex-wrap items-end justify-between gap-2">
            <div>
              <h1 className="flex items-center gap-2 text-xl font-bold text-slate-800 dark:text-gray-100"><CheckSquare size={20} className="text-[#3e91ab]" />{viewDef.label}{project && <span className="font-normal text-slate-500">· {project.name}</span>}</h1>
              <p className="text-sm text-slate-500">{viewDef.hint}</p>
            </div>
            <Button className="lg:!hidden" type="primary" icon={<Plus size={16} />} onClick={() => setCreating(true)}>Tạo công việc</Button>
          </div>

          <div className="flex flex-wrap items-center gap-2 lg:hidden">
            <Select className="w-48" value={view} onChange={v => setView(v)} options={VIEWS.map(v => ({ value: v.key, label: `${v.label} (${counts[v.key]})` }))} />
            <Select className="w-60" allowClear placeholder="Tất cả dự án" value={projectId} onChange={v => setParam('project', v)} options={PROJECTS.map(p => ({ value: p.id, label: p.name }))} />
          </div>

          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white p-2.5 dark:border-gray-700 dark:bg-gray-800">
            <Input.Search allowClear placeholder="Tìm mã, tên công việc…" className="w-60" value={q} onChange={e => setQ(e.target.value)} />
            {layout !== 'board' && (
              <Segmented value={statusFilter} onChange={v => setStatusFilter(v as StatusFilter)}
                options={STATUS_FILTERS.map(s => ({ value: s.value, label: `${s.label} (${statusCount(s.value)})` }))} />
            )}
            {view !== 'mine' && <Select className="w-52" allowClear showSearch optionFilterProp="label" placeholder="Người làm" value={assignee} onChange={setAssignee} options={memberOptions()} />}
            <Segmented className="ml-auto" value={layout} onChange={v => setLayout(v as typeof layout)}
              options={[{ value: 'list', label: 'Danh sách' }, { value: 'board', label: 'Bảng' }, { value: 'gantt', label: 'Gantt' }]} />
          </div>

          {layout === 'list' && (
            <div className="rounded-xl border border-slate-200 bg-white dark:border-gray-700 dark:bg-gray-800">
              <Table<WorkTask> rowKey="id" size="middle" columns={columns} dataSource={shown} scroll={{ x: 'max-content' }}
                rowClassName={t => (isOverdue(t) ? 'cursor-pointer bg-red-50/40' : 'cursor-pointer')}
                onRow={t => ({ onClick: () => openTask(t.id) })}
                pagination={{ pageSize: 20, hideOnSinglePage: true, showSizeChanger: false, showTotal: n => `${n} công việc` }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={view === 'approve' ? 'Không có việc nào chờ bạn duyệt' : 'Không có công việc'} /> }} />
            </div>
          )}
          {layout === 'board' && <div className="overflow-x-auto pb-2"><Board tasks={shown} onOpen={openTask} /></div>}
          {layout === 'gantt' && <GanttView tasks={shown.filter(t => t.status !== 'cancelled')} onOpen={openTask} />}
        </main>
      </div>

      <TaskDetail taskId={taskId} onClose={() => setParam('task', null)} onOpen={openTask} />
      <TaskForm open={creating} onClose={() => setCreating(false)} projectId={projectId} onCreated={t => openTask(t.id)} />
    </Layout>
  );
}
